/**
 * Download Folder Action
 * Downloads all files in a folder as a zip using the backend folder download API
 */

import { Datum } from "../datastructure";
import { isProcessingFolder } from "../specialFolders";
import { ActionDefinition, ActionContext } from "./types";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";

/**
 * Downloads a folder by path using the backend API
 */
export async function downloadFolderByPath(
  folderPath: string[],
  driveId: string | null,
  filename: string
): Promise<void> {
  const response = await fpFetch(`${BASE_API_URL_V2}/api/v2/tasks/download`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ folderPath, driveId, filename }),
  });

  if (!response.ok) {
    throw new Error(`Failed to download folder: ${response.statusText}`);
  }

  // Get the blob and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;

  // Get filename from response header or use provided filename
  const contentDisposition = response.headers.get("Content-Disposition");
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(contentDisposition || "");
  const downloadFilename =
    matches && matches[1] ? matches[1].replace(/['"]/g, "") : filename;

  a.download = downloadFilename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Downloads all files in a folder as a zip
 */
export async function downloadFolder(
  datum: Datum,
  context: ActionContext
): Promise<void> {
  if (datum.type !== "folder") {
    console.error("downloadFolder: Cannot download non-folder datum");
    return;
  }

  try {
    // In across-drives mode WITH drives enabled, the first path segment is the driveId
    const folderPath = context.filetree?.shouldStripDrivePrefix
      ? datum.path.slice(1)
      : datum.path;
    const driveId = datum.payload?.driveId ?? context.driveId ?? null;
    const folderName = datum.name || "folder";

    await downloadFolderByPath(folderPath, driveId, `${folderName}.zip`);
  } catch (error) {
    console.error("downloadFolder: Failed to download folder", error);
    context.showToast?.("Failed to download folder", "error");
  }
}

/**
 * Action definition for context menu
 */
export const downloadFolderAction: ActionDefinition = {
  config: {
    action: "download-folder",
    label: "Download Folder",
  },
  // The synthetic Processing folder is excluded: its membership changes under
  // the user as placement runs, and the backend refuses to zip it (#11257).
  isApplicable: (datum) =>
    datum.type === "folder" &&
    datum.path.length > 0 &&
    !isProcessingFolder(datum),
  execute: downloadFolder,
};
